import { FC, HTMLAttributes, useEffect } from "react";
import clsx from "classnames";
import { Typography } from "../../core/Typography/Typography";
import { TypographyVariant } from "../../../constants/typography";
import { CoinNumber } from "../../core/CoinNumber/CoinNumber";
import { DURATION_COIN_ANIMATION } from "../../../constants/animation";
import { useStyles } from "./DailyRewardHeader.style";
import { useWalletConfig } from "../../../hooks/useWalletConfig";
import { getWalletBalance } from "../../../constants/wallet";

interface IDailyRewardHeader {
  sizeCoin?: number;
  sizeTypo?: TypographyVariant;
  idCoin?: string;
  nbCoin: number;
  coinAdd?: number;
  coinWallets?: { itemType: string; balance: number }[];
  secondaryBalances?: Partial<Record<string, number>>;
}

export const DailyRewardHeader: FC<
  HTMLAttributes<HTMLDivElement> & IDailyRewardHeader
> = (props) => {
  const classes = useStyles();
  const {
    sizeCoin,
    sizeTypo,
    idCoin,
    nbCoin,
    coinAdd,
    className,
    coinWallets,
    secondaryBalances,
    ...otherProps
  } = props;
  const { walletConfig } = useWalletConfig();
  const baseCoinType = walletConfig.primaryCurrency.base;

  useEffect(() => {
    if (!coinAdd || !idCoin) return;
    const element = document.getElementById("coin-target-nb");
    let animationFrame: number;
    if (element) {
      const start = Number(element.textContent || 0);
      const end = start + coinAdd;
      const startTime = Date.now();
      const animate = () => {
        const elapsed = Date.now() - startTime;
        const progress = Math.min(elapsed / DURATION_COIN_ANIMATION, 1);
        const current = Math.floor(start + (end - start) * progress);
        element.textContent = String(current);
        if (progress < 1) {
          animationFrame = requestAnimationFrame(animate);
        }
      };
      animate();
    }
    return () => cancelAnimationFrame(animationFrame);
  }, [coinAdd, idCoin]);

  return (
    <div {...otherProps} className={clsx(classes.root, className)}>
      {walletConfig.secondaryCurrencies.map((coinType) => {
        const balance = secondaryBalances?.[coinType] ?? getWalletBalance(coinWallets ?? [], coinType);
        return (
          <Typography key={coinType} variant={sizeTypo || TypographyVariant.BodyB6Bold}>
            <CoinNumber type={coinType} amount={balance} size={sizeCoin || 24} />
          </Typography>
        );
      })}
      <Typography variant={sizeTypo || TypographyVariant.BodyB6Bold}>
        <CoinNumber
          type={baseCoinType}
          amount={nbCoin}
          size={sizeCoin || 24}
          idCoin={idCoin}
        />
      </Typography>
    </div>
  );
};
